import React, { useState } from 'react';
import { methodClass } from './CapabilityRunner';

/**
 * Editing the capability doc as fields, not as JSON.
 *
 * The tab used to collect `endpoints` in a <textarea> of raw JSON. The CLI
 * validates this schema properly — one endpoint may be the health check, it has
 * to be GET or HEAD, `expect.status` is 100-599 — and a textarea can hold none
 * of that line. So a developer authored blind and found out at Save, and an app
 * with nothing declared saw only the textarea, which is the state almost every
 * app is actually in.
 *
 * The array is the source of truth here; JSON is a one-way import in the drawer
 * below, so there is no two-way text/object sync to get wrong.
 */

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const SCHEME_TYPES = ['none', 'api_key', 'bearer', 'oauth2', 'basic', 'hmac', 'custom'];
const PARAM_IN = ['query', 'path', 'header', 'body'];

/** What `paradigm capabilities init` scaffolds, so the two agree. */
export const HEALTH_TEMPLATE = {
  name: 'Liveness',
  description: 'Returns 200 while the app is serving.',
  method: 'GET',
  url: '',
  auth: 'none',
  health: true,
  expect: { status: 200 },
};

export const BLANK_ENDPOINT = {
  name: '', description: '', method: 'GET', url: '', auth: 'none', params: [],
};

/* The same rules the CLI and the backend enforce, shown while you type rather
 * than at Save. Mirrors _validate_probe in capability_config.py. */
export function endpointProblems(ep, all, schemes) {
  const out = [];
  if (!ep.name) out.push('needs a name');
  if (!ep.url) out.push('needs a URL');
  const method = String(ep.method || 'GET').toUpperCase();
  if (ep.health) {
    if (!['GET', 'HEAD'].includes(method)) {
      out.push('the health check must be GET or HEAD — the platform calls it on a schedule');
    }
    if (ep.auth && ep.auth !== 'none') {
      out.push('the health check must be reachable with auth: none — the prober holds no credential of yours');
    }
    if (all.filter(e => e.health).length > 1) out.push('only one endpoint can be the health check');
  }
  if (ep.auth && ep.auth !== 'none' && !(schemes || {})[ep.auth]) {
    out.push(`auth '${ep.auth}' is not a scheme declared above`);
  }
  const status = ep.expect?.status;
  if (status !== undefined && status !== '' && status !== null) {
    const n = Number(status);
    if (!Number.isInteger(n) || n < 100 || n > 599) out.push('expect.status must be 100-599');
  }
  return out;
}

function Field({ label, hint, children, wide }) {
  return (
    <div className={`ce-field${wide ? ' ce-field--wide' : ''}`}>
      <label>{label}</label>
      {children}
      {hint && <span className="ce-hint">{hint}</span>}
    </div>
  );
}

function EndpointForm({ ep, index, all, schemes, onChange, onRemove }) {
  const set = (patch) => onChange({ ...ep, ...patch });
  const setExpect = (patch) => {
    const next = { ...(ep.expect || {}), ...patch };
    for (const k of Object.keys(next)) if (next[k] === '' || next[k] == null) delete next[k];
    onChange({ ...ep, expect: Object.keys(next).length ? next : undefined });
  };
  const problems = endpointProblems(ep, all, schemes);
  const method = String(ep.method || 'GET').toUpperCase();

  return (
    <div className="ce-ep">
      <div className="ce-ep__head">
        <span className={`cap-verb cap-verb--${methodClass(method)}`}>{method}</span>
        <strong>{ep.name || <em>Untitled endpoint</em>}</strong>
        {ep.health && <span className="cap-ep__tag">health</span>}
        <button className="ce-remove" onClick={onRemove} aria-label={`Remove ${ep.name || 'endpoint'}`}>
          Remove
        </button>
      </div>

      <div className="ce-grid">
        <Field label="Name">
          <input value={ep.name || ''} onChange={e => set({ name: e.target.value })}
                 placeholder="List a user's missions" />
        </Field>
        <Field label="Method">
          <select value={method} onChange={e => set({ method: e.target.value })}>
            {METHODS.map(m => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="URL" wide>
          <input value={ep.url || ''} onChange={e => set({ url: e.target.value })}
                 placeholder="https://your.app/v1/missions" />
        </Field>
        <Field label="Description" wide>
          <input value={ep.description || ''} onChange={e => set({ description: e.target.value })}
                 placeholder="What this endpoint does." />
        </Field>
        {/* The list is `none` plus the schemes declared above, which is not
            visible from here — so an app with one scheme looks like a product
            that only supports that one kind of auth. */}
        <Field
          label="Auth"
          hint={Object.keys(schemes || {}).length
            ? 'Bearer, API key, OAuth2 and the rest are types — add them under Auth schemes above.'
            : 'No schemes declared yet. Add one under Auth schemes above to pick it here.'}
        >
          <select value={ep.auth || 'none'} onChange={e => set({ auth: e.target.value })}>
            <option value="none">none</option>
            {Object.keys(schemes || {}).map(id => <option key={id} value={id}>{id}</option>)}
          </select>
        </Field>
        <Field label="Health check"
               hint="The one endpoint ofSelf probes on a schedule.">
          <label className="ce-check">
            <input type="checkbox" checked={!!ep.health}
                   onChange={e => set({ health: e.target.checked || undefined })} />
            <span>this is it</span>
          </label>
        </Field>

        <Field label="Expect status"
               hint="What a correct answer returns.">
          <input type="number" value={ep.expect?.status ?? ''} placeholder="200"
                 onChange={e => setExpect({ status: e.target.value === '' ? '' : Number(e.target.value) })} />
        </Field>
        <Field label="Expect JSON at"
               hint="Optional. e.g. $.missions[*].id">
          <input value={ep.expect?.json ?? ''} placeholder="$.missions[*].id"
                 onChange={e => setExpect({ json: e.target.value })} />
        </Field>
      </div>

      <div className="ce-params">
        <div className="ce-params__head">
          <span>Parameters</span>
          <button className="cap-btn cap-btn--ghost"
                  onClick={() => set({ params: [...(ep.params || []), { name: '', in: 'query' }] })}>
            Add parameter
          </button>
        </div>
        {(ep.params || []).map((p, i) => (
          <div className="ce-param" key={i}>
            <input value={p.name || ''} placeholder="name"
                   onChange={e => set({ params: ep.params.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
            <select value={p.in || 'query'}
                    onChange={e => set({ params: ep.params.map((x, j) => j === i ? { ...x, in: e.target.value } : x) })}>
              {PARAM_IN.map(v => <option key={v}>{v}</option>)}
            </select>
            <label className="ce-check">
              <input type="checkbox" checked={!!p.required}
                     onChange={e => set({ params: ep.params.map((x, j) => j === i ? { ...x, required: e.target.checked || undefined } : x) })} />
              <span>required</span>
            </label>
            <button className="ce-remove"
                    onClick={() => set({ params: ep.params.filter((_, j) => j !== i) })}>Remove</button>
          </div>
        ))}
      </div>

      {problems.length > 0 && (
        <ul className="ce-problems">
          {problems.map(p => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}

function SchemeForm({ id, scheme, onRename, onChange, onRemove }) {
  const set = (patch) => onChange({ ...scheme, ...patch });
  return (
    <div className="ce-scheme">
      <div className="ce-grid">
        <Field label="Scheme id" hint="Endpoints reference this.">
          <input value={id} onChange={e => onRename(e.target.value)} placeholder="api_key" />
        </Field>
        <Field label="Type">
          <select value={scheme.type || 'api_key'} onChange={e => set({ type: e.target.value })}>
            {SCHEME_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Sent in">
          <select value={scheme.location || 'header'} onChange={e => set({ location: e.target.value })}>
            <option value="header">header</option>
            <option value="query">query</option>
          </select>
        </Field>
        <Field label="Header / param name">
          <input value={scheme.name || ''} onChange={e => set({ name: e.target.value })}
                 placeholder="X-API-Key" />
        </Field>
        <Field label="How a caller gets one" wide
               hint="An agent reads this to work out whether it can authenticate at all.">
          <input value={scheme.how_to || ''} onChange={e => set({ how_to: e.target.value })}
                 placeholder="Issued per user at authorize time; see /docs/auth." />
        </Field>
      </div>
      <button className="ce-remove" onClick={onRemove}>Remove scheme</button>
    </div>
  );
}

export default function CapabilityEditor({ endpoints, schemes, onEndpoints, onSchemes }) {
  const [importText, setImportText] = useState('');
  const [importErr, setImportErr] = useState('');

  const list = endpoints || [];
  const hasHealth = list.some(e => e.health);

  const addScheme = () => {
    const base = 'api_key';
    let id = base, n = 2;
    while ((schemes || {})[id]) id = `${base}_${n++}`;
    onSchemes({ ...(schemes || {}), [id]: { type: 'api_key', location: 'header', name: 'X-API-Key' } });
  };

  const renameScheme = (from, to) => {
    if (!to || to === from) return;
    const next = {};
    for (const [k, v] of Object.entries(schemes || {})) next[k === from ? to : k] = v;
    onSchemes(next);
    // An endpoint pointing at the old id would silently become invalid.
    onEndpoints(list.map(e => (e.auth === from ? { ...e, auth: to } : e)));
  };

  const doImport = () => {
    try {
      const parsed = JSON.parse(importText);
      if (Array.isArray(parsed)) onEndpoints(parsed);
      else if (parsed && typeof parsed === 'object' && Array.isArray(parsed.endpoints)) {
        onEndpoints(parsed.endpoints);
        if (parsed.auth_schemes) onSchemes(parsed.auth_schemes);
      } else throw new Error('expected an array of endpoints, or a doc with an "endpoints" array');
      setImportText('');
      setImportErr('');
    } catch (e) {
      setImportErr(String(e.message || e));
    }
  };

  return (
    <div className="ce">
      <section className="ce-sec">
        <div className="ce-sec__head">
          <h4>Auth schemes</h4>
          <button className="cap-btn cap-btn--ghost" onClick={addScheme}>Add scheme</button>
        </div>
        {Object.keys(schemes || {}).length === 0 ? (
          <p className="ce-empty">
            None yet. Add one only if an endpoint needs a credential — a health check must not.
          </p>
        ) : (
          Object.entries(schemes).map(([id, s]) => (
            <SchemeForm
              key={id}
              id={id}
              scheme={s}
              onRename={(to) => renameScheme(id, to)}
              onChange={(next) => onSchemes({ ...schemes, [id]: next })}
              onRemove={() => {
                const next = { ...schemes };
                delete next[id];
                onSchemes(next);
              }}
            />
          ))
        )}
      </section>

      <section className="ce-sec">
        <div className="ce-sec__head">
          <h4>Endpoints</h4>
          {!hasHealth && (
            <button
              className="cap-btn"
              onClick={() => onEndpoints([{ ...HEALTH_TEMPLATE }, ...list])}
            >
              Add a health endpoint
            </button>
          )}
          <button className="cap-btn cap-btn--ghost"
                  onClick={() => onEndpoints([...list, { ...BLANK_ENDPOINT }])}>
            Add endpoint
          </button>
        </div>

        {list.length === 0 && (
          <p className="ce-empty">
            None yet. Start with the health check — it is the only declaration that changes what
            the rest of the platform knows about this app.
          </p>
        )}

        {list.map((ep, i) => (
          <EndpointForm
            key={i}
            ep={ep}
            index={i}
            all={list}
            schemes={schemes}
            onChange={(next) => onEndpoints(list.map((x, j) => (j === i ? next : x)))}
            onRemove={() => onEndpoints(list.filter((_, j) => j !== i))}
          />
        ))}
      </section>

      <details className="ce-import">
        <summary>Paste JSON instead</summary>
        <p className="ce-hint">
          Replaces everything above. One-way on purpose — a textarea kept in sync with these
          fields is a bug waiting to happen, and <code>paradigm capabilities push</code> is the
          better road for a doc of any size.
        </p>
        <textarea
          value={importText}
          onChange={e => setImportText(e.target.value)}
          spellCheck={false}
          placeholder='[{"name": "Liveness", "method": "GET", "url": "https://your.app/healthz", "auth": "none", "health": true, "expect": {"status": 200}}]'
        />
        {importErr && <p className="ce-problems">{importErr}</p>}
        <button className="cap-btn cap-btn--ghost" onClick={doImport} disabled={!importText.trim()}>
          Replace from JSON
        </button>
      </details>
    </div>
  );
}
